import type { ReactNode } from 'react';
import React, { Component } from 'react';
import API from '@api';
import Crash from './components/Crash';
import {
  isChunkLoadError,
  isDeployReloadPending,
  reloadOnceAfterDeploy
} from '@utils/deployRecovery';

if (!('toJSON' in Error.prototype)) {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(Error.prototype, 'toJSON', {
    value: function () {
      const alt = {};

      Object.getOwnPropertyNames(this).forEach(function (key) {
        alt[key] = this[key];
      }, this);

      return alt;
    },
    configurable: true,
    writable: true
  });
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  /**
   * Ошибка про недоехавший чанк: код цел, у вкладки просто устарели адреса
   * файлов (см. utils/deployRecovery). Пока идёт перезагрузка, рисуем
   * пустоту вместо экрана краха — иначе он мигнёт и пропадёт.
   */
  recovering: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, recovering: false };

  // ЭТАП 1 · Шаг 3 — в SSR бордер восстанавливается ТОЛЬКО через это (commit-
  // фазный componentDidCatch на сервере не вызывается). Без него любая ошибка
  // рендера пролетала в 500.
  static getDerivedStateFromError(error: Error): State {
    // Либо это сама ошибка чанка, либо любая ошибка, прилетевшая уже ПОСЛЕ
    // старта перезагрузки: пока страница уезжает, показывать краш-экран
    // нечестно — он всё равно мигнёт и исчезнет
    return {
      hasError: true,
      recovering: isChunkLoadError(error) || isDeployReloadPending()
    };
  }

  componentDidCatch(error: Error, info: any): void {
    // Недоехавший чанк — не баг кода, а последствие деплоя: краш-репорт
    // отправлять не о чем, лечится одной перезагрузкой. Если перезагрузку
    // не разрешил ограничитель (уже пробовали), показываем обычный экран
    // ошибки — это честнее бесконечного моргания
    if (isDeployReloadPending()) {
      // Перезагрузка уже идёт — это агония старого документа, репортить
      // нечего и делать тоже
      return;
    }
    if (isChunkLoadError(error)) {
      const started = reloadOnceAfterDeploy('ErrorBoundary');
      this.setState({ hasError: true, recovering: started });
      return;
    }

    // SSR-safe: на сервере window/navigator/screen нет — не строим краш-репорт.
    if (typeof window === 'undefined') {
      this.setState({ hasError: true });
      return;
    }

    const logError: any = {
      ...error,
      url: location.href,
      navigator: navigator.userAgent,
      screen: {
        width: window.screen?.width,
        height: window.screen?.height
      }
    };

    if (!logError?.url?.includes('localhost')) {
      API.setCrash({
        error: JSON.stringify(logError),
        info: JSON.stringify(info)
      });
    }

    this.setState({ hasError: true });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return this.state.recovering ? null : <Crash />;
    }
    return this.props.children;
  }
}
